import apiEndpoint from '../../utils/apiEndpoint'
import AbstractApiClient from './AbstractApiClient'
import ChannelDefaultRate from '../../models/ChannelDefaultRate'
import { DefaultRate } from '../../models/DefaultRate'
import { Inventory } from '../../models/Inventory'
import { SpotRate } from '../../models/SpotRate'
import { Temporal } from '@js-temporal/polyfill'

const API_URL = import.meta.env.VITE_API_URL

/**
 * Provides API endpoints which deal with more than one model.
 */
class RatesApi extends AbstractApiClient {

  /**
   * @deprecated User DefaultRate.getByLocationId() instead.
   */
  fetchByLocationID(locationID: number, options: FetchRatesByLocationIdOptions, signal?: AbortSignal) {

    let url = `/rates/locations/${locationID}/rates`

    url += RatesApi.buildQuerystring(options, [
      'verticalType',
      'start',
      'end',
      'include_event_tiers',
    ])

    return apiEndpoint({
      url: `${API_URL}${url}`,
      method: 'get',
      responseHandler: (response) => {

        const promises = []

        promises.push(
          DefaultRate.insert({
            data: response.data.default_rates,
          })
        )
        promises.push(
          ChannelDefaultRate.insert({
            data: response.data.channel_default_rates,
          })
        )
        promises.push(
          SpotRate.insert({
            data: response.data.spot_rates,
          })
        )
        if (
          'inventories_with_event_tiers' in response.data &&
          Array.isArray(response.data.inventories_with_event_tiers) &&
          response.data.inventories_with_event_tiers.length
        ) {
          Inventory.insert({
            data: response.data.inventories_with_event_tiers
          })
        }

        return Promise.all(promises)
          .then((results) => {
          
            // Seems kinda dumb to re-query for models that we already have,
            // but I couldn't find a more straightforward way to load relations
            // for existing models.

            let insertedDefaultRateIDs: number[] = []
            if ('default_rates' in results[0]) {
              insertedDefaultRateIDs = results[0].default_rates.map((defaultRate) => (defaultRate as DefaultRate).ID)
            }

            let insertedSpotRateIDs: (number | string)[] = []
            if ('spot_rates' in results[2]) {
              insertedSpotRateIDs = results[2].spot_rates.map((spotRate) => (spotRate as SpotRate).ID)
            }

            const defaultRates = DefaultRate.query()
              .whereIdIn(insertedDefaultRateIDs)
              .with(['channelDefaultRates', 'channelDefaultRates.channelFacility'])
              .with('spotRates', (query) => {
                query.with('channelFacility').whereIdIn(insertedSpotRateIDs)
              })
              .all()

            if (options.include_event_tiers) {
              return { defaultRates, inventoriesWithEventTiers: response.data.inventories_with_event_tiers}
            } else {
              return defaultRates
            }
              
          })

      },
    }, signal)

  }

  updateSpotHeroIQRates(locationID: number, policies: UpdateSpotHeroIQRatesRequest['policies']) {

    const payload: UpdateSpotHeroIQRatesRequest = {policies: policies}

    return apiEndpoint({
      url: `${API_URL}/rates/spothero-iq/${locationID}`,
      method: 'put',
      payload: payload,
    })
    
  }

  triggerAllRates(locationID: number, productType: string) {
    return apiEndpoint({
      url: `${API_URL}/rates/locations/${locationID}/trigger_all`,
      payload: {productType: productType},
      method: 'post',
    })
  }

  syncSpotRates(request: SyncSpotRatesRequest): Promise<SyncSpotRatesResponse> {

    return apiEndpoint({
      url: `${API_URL}/rates/spot_rate_sync`,
      payload: request,
      method:'post',
    }).then(response => {

      const hydrateDates = ((sr: any) => {
        sr.start_date = Temporal.PlainDate.from(sr.start_date)
        sr.end_date = Temporal.PlainDate.from(sr.end_date)
        return sr
      })

      const hydratedResponse: SyncSpotRatesResponse = {
        created: (response?.created ?? []).map(hydrateDates),
        updated: (response?.updated ?? []).map(hydrateDates),
        deleted: (response?.deleted ?? []).map(hydrateDates),
      }

      hydratedResponse.deleted.forEach(sr => SpotRate.delete(sr.id))
      return hydratedResponse

    })

  }
  
}

export default new RatesApi()

export interface UpdateSpotHeroIQRatesRequest {
  policies: {
    policy: string
    threshold: null | number
  }[]
  bypassWorkers?: boolean
}

export interface FetchRatesByLocationIdOptions {
  verticalType?: string
  start?: string
  end?: string
  include_event_tiers?: boolean
}

export interface SyncSpotRatesRequest {
  spotRates: SyncSpotRatesRequestItem[]
}

export interface SyncSpotRatesRequestItem {
  date: Temporal.PlainDate
  locationProductId: number
  defaultRateId: number
  channelDefaultRateId: number
  channelCost: number
}

export interface SyncSpotRatesResponse {
  created: {
    default_rate_id: number
    start_date: Temporal.PlainDate
    end_date: Temporal.PlainDate
    channel_spot_rates: {
      channel_facility_id: number
      spot_cost: number
    }[]
    bypass_workers: boolean
  }[]
  updated: {
    id: number
    default_rate_id: number
    start_date: Temporal.PlainDate
    end_date: Temporal.PlainDate
    spot_cost: number
    bypass_workers: boolean
  }[]
  deleted: {
    id: number
    default_rate_id: number
    start_date: Temporal.PlainDate
    end_date: Temporal.PlainDate
    bypass_workers: boolean
  }[]
}
